import apiClient from './api';
import type {
  EstructuraArmarPedido,
  GuardarBorradorRequest,
  GuardarBorradorResponse,
  EstadisticasPedidos,
  FiltrosPedidosNuevos,
  RespuestaListadoPedidos,
  PedidoDetallado,
  CambiarEstadoResponse,
  BorradorPendiente,
} from '../types/pedidosNuevos';

/**
 * Servicio API para el nuevo sistema de pedidos
 */
export class PedidosNuevosAPI {
  /**
   * GET /api/pedidos-nuevos/armar
   * Obtener estructura de minuta para armar pedido
   */
  static async obtenerEstructuraArmarPedido(
    clienteId: number,
    minutaId: number,
    operacionId?: number
  ): Promise<EstructuraArmarPedido> {
    const params: Record<string, number> = { clienteId, minutaId };
    if (operacionId !== undefined) params.operacionId = operacionId;
    const response = await apiClient.get('/pedidos-nuevos/armar', { params });

    return response.data.data;
  }

  /**
   * GET /api/pedidos-nuevos/estadisticas
   * Obtener estadísticas de pedidos
   */
  static async obtenerEstadisticas(
    clienteId: number,
    minutaId: number
  ): Promise<EstadisticasPedidos> {
    const response = await apiClient.get('/pedidos-nuevos/estadisticas', {
      params: { clienteId, minutaId },
    });

    return response.data.data;
  }

  /**
   * POST /api/pedidos-nuevos/guardar-borrador
   * Guardar o actualizar borrador de pedido para un día
   */
  static async guardarBorrador(
    data: GuardarBorradorRequest
  ): Promise<GuardarBorradorResponse> {
    const response = await apiClient.post(
      '/pedidos-nuevos/guardar-borrador',
      data
    );

    return response.data;
  }

  /**
   * PUT /api/pedidos-nuevos/:id/confirmar
   * Confirmar un pedido (cambiar de borrador a solicitado)
   */
  static async confirmarPedido(pedidoId: number): Promise<{
    success: boolean;
    message: string;
    data: {
      pedidoId: number;
      fecha: string;
      estado: string;
    };
  }> {
    const response = await apiClient.put(
      `/pedidos-nuevos/${pedidoId}/confirmar`
    );

    return response.data;
  }

  /**
   * PUT /api/pedidos-nuevos/:id/modificar
   * Permitir modificar un pedido confirmado (vuelve a borrador)
   */
  static async modificarPedido(pedidoId: number): Promise<{
    success: boolean;
    message: string;
    data: {
      pedidoId: number;
      estado: string;
    };
  }> {
    const response = await apiClient.put(
      `/pedidos-nuevos/${pedidoId}/modificar`
    );

    return response.data;
  }

  /**
   * GET /api/pedidos-nuevos/listar
   * Listar pedidos con filtros y paginación
   */
  static async listarPedidos(
    filtros: FiltrosPedidosNuevos = {}
  ): Promise<RespuestaListadoPedidos> {
    const response = await apiClient.get('/pedidos-nuevos/listar', {
      params: filtros,
    });

    return response.data;
  }

  /**
   * GET /api/pedidos-nuevos/:id
   * Obtener detalle completo de un pedido
   */
  static async obtenerDetalle(pedidoId: number): Promise<PedidoDetallado> {
    const response = await apiClient.get(`/pedidos-nuevos/${pedidoId}`);

    return response.data.data;
  }

  /**
   * GET /api/pedidos-nuevos/borradores-pendientes
   * Devuelve borradores en minutas vigentes filtrados por rol
   * (cliente → propios; admin → todos; chef/cocina → 403).
   * Para el banner "Pedidos por confirmar" en Armar Pedido.
   */
  static async obtenerBorradoresPendientes(): Promise<{
    success: boolean;
    data: BorradorPendiente[];
    total: number;
  }> {
    const response = await apiClient.get(
      '/pedidos-nuevos/borradores-pendientes'
    );
    return response.data;
  }

  /**
   * PUT /api/pedidos-nuevos/:id/estado
   * Cambiar estado de un pedido
   */
  static async cambiarEstado(
    pedidoId: number,
    nuevoEstado: string,
    motivoRechazo?: string
  ): Promise<CambiarEstadoResponse> {
    const response = await apiClient.put(`/pedidos-nuevos/${pedidoId}/estado`, {
      nuevoEstado,
      motivoRechazo,
    });

    return response.data;
  }
}
